/**
 * لوحة مؤشرات السوق — العربية.
 *
 * العربية هي اللغة الافتراضية للموقع، فهذا الملف هو ما يقرأه أغلب المستخدمين.
 *
 * قاعدة الكتابة هنا: جملة قصيرة، كلمة شائعة، ومعنى واضح من أول قراءة.
 * النسخة الأولى كانت أدبية أكثر من اللازم («مقيساً لا مُدّعى»، «كم يحتمل سجلّه من ثقة»)
 * فصارت غامضة. كل نص هنا يشرح ما تفعله الأداة، لا يصفها وصفاً بلاغياً.
 *
 * القواعد الملزمة (CONTEXT.md §8.5):
 *   - ثلاث صيغ محايدة فقط للسعر: ضمن النطاق المعتاد / أعلى من أغلب الصفقات /
 *     أدنى من أغلب الصفقات. ممنوع «مبالغ فيه» أو «صفقة رابحة».
 *   - العائد إجمالي دائماً، مع ذكر أنه قبل التكاليف وضريبة التصرفات 5%.
 *   - عدد الصفقات والفترة الزمنية بجانب الرقم، لا في هامش صغير.
 *   - لا يُسمّى أي شيء «تقييماً»: التقييم العقاري نشاط مرخّص في السعودية.
 */

const marketConsole = {
  common: {
    source: 'وزارة العدل - الصفقات العقارية المسجّلة',
    disclaimer: 'مؤشر إحصائي استرشادي - ليس تقييماً',
    how: 'كيف تعمل هذه الأداة؟',
    loading: 'جاري تحميل البيانات',
    none: 'لا توجد نتائج',
    sar: 'ريال',
    m2: 'م²',
    perM2: 'ريال/م²',
    retry: 'أعد المحاولة',
    adminOnly: 'للمشرفين فقط',
  },

  // الفترات الزمنية. كل أداة تستخدم مدة مختلفة، وهذه التسميات تجعل ذلك واضحاً.
  period: {
    measuredOver: 'الفترة',
    comparedWith: 'مقارنة بـ',
    upTo: 'كل الصفقات حتى',
    rentUpTo: 'بيانات الإيجار حتى',
    pricesFrom: 'أسعار مسجّلة من',
    since: 'أول ظهور من',
    thisQuarter: 'آخر ربع',
    forecastFor: 'التوقّع لـ',
    published: 'نشرته الهيئة في',
  },

  masthead: {
    eyebrow: 'بيانات رسمية',
    title: 'مؤشرات السوق',
    subtitle:
      'أرقام مبنية على صفقات عقارية مسجّلة فعلاً لدى وزارة العدل. كل سعر يظهر كنطاق، ومعه عدد الصفقات والفترة الزمنية التي بُني عليها. وإذا كانت البيانات غير كافية نقول ذلك بوضوح بدل تخمين رقم.',
    districts: 'حي له أسعار',
    rentDistricts: 'حي له بيانات إيجار',
    deeds: 'صفقة مسجّلة',
    shortlist: 'حي في قائمة الأراضي',
  },

  nav: {
    a: 'عقارك',
    b: 'معلومات الحي',
    c: 'أين تشتري',
    d: 'تستأجر أم تشتري',
    e: 'حركة الأسعار',
    f: 'دقة البيانات',
  },

  group: {
    a: 'كم يساوي عقارك، وكم يمكن أن يؤجَّر، وكيف يتغيّر السعر مع المساحة.',
    b: 'أربع معلومات عن حي واحد: هل بياناته كافية، وهل ما زال أرضاً أم صار مبنياً، وحركة الربع القادم، وأثر قرب المترو.',
    c: 'إلى أين تصل ميزانيتك، وأحياء شبيهة بسعر أقل، وأفضل شهر للبيع.',
    d: 'العائد الإجمالي فقط. لم نخصم رسوم الخدمات ولا الصيانة ولا فترات الشغور ولا ضريبة التصرفات العقارية 5%، لأن هذه البيانات لا تحتوي عليها أصلاً.',
    e: 'أين زادت قيمة الصفقات، وكم تبلغ أسعار الفلل، وأي الأحياء مناسبة لشراء أرض.',
    f: 'ما استبعدناه من التقارير ولماذا. أي شخص يستطيع تحميل نفس الملفات الحكومية، لكن قليلاً من ينشر ما استبعده منها.',
  },

  place: {
    city: 'المدينة',
    district: 'الحي',
    pickCity: 'اختر مدينة',
    pickCityFirst: 'اختر المدينة أولاً',
    allCities: 'كل المدن',
    search: 'اكتب اسم الحي',
    loading: 'جاري التحميل…',
    noMatch: 'لا يوجد حي بهذا الاسم',
    nDistricts_zero: 'لا توجد أحياء ببيانات كافية',
    nDistricts_one: 'حي واحد ببيانات كافية',
    nDistricts_two: 'حيّان ببيانات كافية',
    nDistricts_few: '{{count}} أحياء ببيانات كافية',
    nDistricts_many: '{{count}} حياً ببيانات كافية',
    nDistricts_other: '{{count}} حي ببيانات كافية',
  },

  segment: {
    res_land: 'أرض سكنية',
    com_land: 'أرض تجارية',
    apartment: 'شقة',
    agri_land: 'أرض زراعية',
    other_built: 'مبني آخر',
    ind_land: 'أرض صناعية',
  },

  rentType: {
    apartment: 'شقة',
    villa: 'فيلا',
    studio: 'استوديو',
    floor: 'دور',
    duplex: 'دوبلكس',
  },

  driver: {
    earlyStage: 'أغلب المبيعات أراضٍ',
    gettingBusier: 'حركة البيع تزيد',
    entryPrice: 'أرخص من متوسط المدينة',
    tradeable: 'كثير البيع والشراء',
    dataSolid: 'بياناته كافية',
  },

  confidence: {
    firm: 'بيانات كافية',
    firmN_zero: 'بيانات كافية',
    firmN_one: 'بيانات كافية · صفقة واحدة',
    firmN_two: 'بيانات كافية · صفقتان',
    firmN_few: 'بيانات كافية · {{count}} صفقات',
    firmN_many: 'بيانات كافية · {{count}} صفقة',
    firmN_other: 'بيانات كافية · {{count}} صفقة',
    indicative: 'بيانات محدودة',
    indicativeN_zero: 'بيانات محدودة',
    indicativeN_one: 'بيانات محدودة · صفقة واحدة',
    indicativeN_two: 'بيانات محدودة · صفقتان',
    indicativeN_few: 'بيانات محدودة · {{count}} صفقات',
    indicativeN_many: 'بيانات محدودة · {{count}} صفقة',
    indicativeN_other: 'بيانات محدودة · {{count}} صفقة',
    extrapolated: 'مساحة غير متداولة هنا',
    contested: 'يختلف عن الصفقات الفعلية',
  },

  rail: {
    aria: 'النطاق من {{low}} إلى {{high}}، ومنتصفه {{mid}}',
    legendModel: 'السعر المتوقّع',
    legendMeasured: 'سعر صفقات فعلية',
    legendMeasuredN_other: 'سعر صفقات فعلية ({{count}} صفقة)',
    // The other Arabic plural forms. i18next does not fall back to `_other`: without
    // them every count except 100-102 rendered the English defaultValue. (Shown only
    // when there is at least one deed, so no `_zero`.)
    legendMeasuredN_one: 'سعر صفقات فعلية (صفقة واحدة)',
    legendMeasuredN_two: 'سعر صفقات فعلية (صفقتان)',
    legendMeasuredN_few: 'سعر صفقات فعلية ({{count}} صفقات)',
    legendMeasuredN_many: 'سعر صفقات فعلية ({{count}} صفقة)',
    legendYours: 'سعرك',
    legendExtrapolated: 'الخط المتقطّع: مساحة لم تُتداول هنا من قبل',
    legendIndicative: 'الخط المتقطّع: عدد الصفقات قليل',
    contested:
      'السعر المتوقّع يختلف عن صفقات الحي الفعلية بأكثر من الضعف. اعتمد على سعر الصفقات الفعلية.',
  },

  verdict: {
    within: {
      title: 'ضمن النطاق المعتاد',
      note: 'سعرك يقع داخل أسعار الصفقات المسجّلة لنفس المساحة في هذا الحي.',
    },
    edge: {
      title: 'على حافة النطاق',
      note: 'سعرك خارج النطاق بفارق بسيط، أقل من 5%.',
    },
    above: {
      title: 'أعلى من أغلب الصفقات المسجّلة',
      note: 'سعرك أعلى من أعلى سعر سُجّل لنفس المساحة في هذا الحي.',
    },
    below: {
      title: 'أدنى من أغلب الصفقات المسجّلة',
      note: 'سعرك أقل من أقل سعر سُجّل لنفس المساحة في هذا الحي.',
    },
    yours: 'سعرك',
    notActionable:
      'عدد الصفقات في هذا الحي قليل، أو المساحة غير متداولة فيه، أو الرقم يختلف عن صفقاته الفعلية. اعتبر هذه النتيجة مؤشراً عاماً فقط.',
  },

  prov: {
    deeds_zero: 'لا توجد صفقات مسجّلة',
    deeds_one: 'صفقة مسجّلة واحدة',
    deeds_two: 'صفقتان مسجّلتان',
    deeds_few: '{{count}} صفقات مسجّلة',
    deeds_many: '{{count}} صفقة مسجّلة',
    deeds_other: '{{count}} صفقة مسجّلة',
    typicalMiss: 'متوسط نسبة الخطأ {{pct}}%',
  },

  evidence: {
    toggle: 'من أين جاء هذا الرقم؟',
    model: 'الطريقة المستخدمة',
    trainedOn: 'بُني على',
    testedOn: 'اختُبر على',
    typicalMiss: 'متوسط نسبة الخطأ',
    cover: 'نسبة وقوع السعر الفعلي داخل النطاق',
    beat: 'قُورن بـ',
    whyThis: 'لماذا اخترنا هذه الطريقة',
    note: 'ملاحظة',
    versus: 'مقابل',
    deedsN_other: '{{count}} صفقة مسجّلة',
    ratesN_other: '{{count}} معدّل إيجار منشور',
    districtsHeldOut_other: '{{count}} حي لم تُستخدم بياناته في البناء',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    // deedsN and ratesN are shown only for a count above zero.
    deedsN_one: 'صفقة مسجّلة واحدة',
    deedsN_two: 'صفقتان مسجّلتان',
    deedsN_few: '{{count}} صفقات مسجّلة',
    deedsN_many: '{{count}} صفقة مسجّلة',
    ratesN_one: 'معدّل إيجار منشور واحد',
    ratesN_two: 'معدّلا إيجار منشوران',
    ratesN_few: '{{count}} معدّلات إيجار منشورة',
    ratesN_many: '{{count}} معدّل إيجار منشور',
    districtsHeldOut_zero: 'لا توجد أحياء خارج بيانات البناء',
    districtsHeldOut_one: 'حي واحد لم تُستخدم بياناته في البناء',
    districtsHeldOut_two: 'حيّان لم تُستخدم بياناتهما في البناء',
    districtsHeldOut_few: '{{count}} أحياء لم تُستخدم بياناتها في البناء',
    districtsHeldOut_many: '{{count}} حياً لم تُستخدم بياناتها في البناء',
  },

  curve: {
    aria: 'تغيّر سعر المتر المربع مع كبر المساحة',
    legendModel: 'السعر المتوقّع للمتر',
    legendMeasured: 'سعر صفقات فعلية',
    legendHollow: 'الدائرة الفارغة: مساحة لم تُتداول هنا',
    axis: 'المحور الأفقي: المساحة بالمتر المربع',
  },

  reason: {
    estimateOff: {
      title: 'بيانات الأسعار غير متاحة حالياً',
      body: 'ملف البيانات غير موجود على الخادم. بقية أقسام الصفحة تعمل بشكل طبيعي.',
    },
    rentOff: {
      title: 'بيانات الإيجار غير متاحة حالياً',
      body: 'ملف البيانات غير موجود على الخادم. بقية أقسام الصفحة تعمل بشكل طبيعي.',
    },
    opportunityOff: {
      title: 'بيانات الميزانية غير متاحة حالياً',
      body: 'ملف البيانات غير موجود على الخادم. بقية أقسام الصفحة تعمل بشكل طبيعي.',
    },
    insightsOff: {
      title: 'بيانات الأحياء غير متاحة حالياً',
      body: 'ملف البيانات غير موجود على الخادم. بقية أقسام الصفحة تعمل بشكل طبيعي.',
    },
    forecastOff: {
      title: 'بيانات التوقّعات غير متاحة حالياً',
      body: 'ملف البيانات غير موجود على الخادم. بقية أقسام الصفحة تعمل بشكل طبيعي.',
    },
    districtUnknown: {
      title: 'لم نجد هذا الحي',
      body: 'اختر حياً من القائمة. نفضّل ألا نجيب على أن نجيب عن حي خاطئ.',
    },
    notEnough: {
      title: 'عدد الصفقات غير كافٍ',
      body: 'الصفقات المسجّلة هنا قليلة جداً، وأي رقم نعطيه سيكون غير دقيق. لذلك لا نعرض شيئاً.',
    },
    segmentUnsupported: {
      title: 'غير متاح لهذا النوع',
      body: 'وزارة العدل تفرّق بين الشقق والأراضي فقط. الفلل والأدوار والتاون هاوس تُسجَّل كلها «أرض»، فلا يمكن استخراج سعر بيع لها. أسعار الفلل تجدها في قسم «حركة الأسعار».',
    },
    segmentRequired: { title: 'اختر نوع العقار', body: 'حدّد النوع لتظهر النتيجة.' },
    areaRequired: { title: 'أدخل المساحة', body: 'السعر يعتمد على المساحة، فنحتاجها.' },
    priceRequired: {
      title: 'أدخل السعر',
      body: 'أضف السعر المطلوب لنقارنه بأسعار الصفقات المسجّلة.',
    },
    sizeOutside: {
      title: 'هذه المساحة غير متداولة في هذا الحي',
      body: 'لم تُسجَّل أي صفقة قريبة من هذه المساحة هنا، فأي رقم سيكون تخميناً.',
    },
    typeUnsupported: {
      title: 'لا يوجد إيجار منشور لهذا النوع',
      body: 'الهيئة العامة للعقار تنشر إيجارات الشقق والفلل والاستوديوهات والأدوار والدوبلكس فقط.',
    },
    budgetRequired: { title: 'أدخل ميزانيتك', body: 'اكتب المبلغ المتاح لديك.' },
    budgetTooLow: {
      title: 'لا يوجد حي بهذه الميزانية',
      body: 'لا يوجد في المملكة حي سعر الدخول فيه بهذا الانخفاض لهذا النوع.',
    },
    noMetro: {
      title: 'لا يوجد مترو في هذه المدينة',
      body: 'الرياض هي المدينة الوحيدة التي لديها شبكة مترو في البيانات.',
    },
    reviewFailed: {
      title: 'تعذّرت المراجعة',
      body: 'حدث خطأ أثناء قراءة بيانات هذا الإعلان.',
    },
    requestFailed: {
      title: 'تعذّر الاتصال بالخادم',
      body: 'لم يصل الطلب. تأكد من الاتصال وأعد المحاولة.',
    },
    forbidden: {
      title: 'هذا القسم للمشرفين',
      body: 'هذه المعلومة تخصّ من يراجع جودة البيانات داخلياً، ولا تُعرض للعامة.',
    },
  },

  a1: {
    title: 'كم يساوي عقاري؟',
    tagline:
      'اختر الحي والمساحة، ونعرض لك نطاق الأسعار التي سُجّلت فعلاً لنفس المساحة في نفس الحي. وإذا أدخلت سعرك، نوضّح أين يقع منها.',
    how: 'أخذنا كل صفقات البيع المسجّلة في المملكة وبنينا منها نموذجاً يحسب سعر المتر المربع حسب الحي ونوع العقار والمساحة. النطاق المعروض ليس تخميناً: قِسناه من مقدار خطأ النموذج الفعلي على ربع كامل لم نُطلعه عليه. وإذا كان الحي قد سجّل صفقات قريبة من مساحتك، نعرضها بجانب الرقم المتوقّع لتقارن بنفسك.',
    coverage: '{{districts}} حي',
    area: 'المساحة',
    areaHint: 'مساحة الأرض أو الشقة',
    areaRequired: 'أدخل المساحة بالمتر المربع',
    areaInvalid: 'المساحة غير صحيحة',
    price: 'السعر المطلوب (اختياري)',
    priceHint: 'أضفه لتعرف أين يقع من أسعار السوق',
    type: 'نوع العقار',
    run: 'اعرض النتيجة',
    idle: 'اختر الحي والمساحة لتظهر النتيجة هنا.',
    steps: [
      'البحث عن الحي في الصفقات المسجّلة',
      'قراءة الصفقات القريبة من مساحتك',
      'حساب السعر المتوقّع',
      'حساب النطاق من نسبة الخطأ المقيسة',
    ],
    resultTitle: '{{area}} م² في حي {{district}}',
    totalTitle: 'السعر الإجمالي المتوقّع',
    totalNote: 'نطاق يغطي 80% من الصفقات المسجّلة. ليس عرض بيع ولا تقييماً.',
    perM2Title: 'سعر المتر المربع',
    measuredLabel: 'صفقات فعلية بمساحة مشابهة',
    yourPrice: 'سعرك',
    extrapolated:
      'المساحات المتداولة في هذا الحي بين {{from}} و {{to}} م². مساحتك خارج هذا المدى، فالرقم تقديري أكثر منه مقيساً.',
  },

  a2: {
    title: 'هل يختلف سعر المتر باختلاف المساحة؟',
    tagline:
      'في أغلب الأحياء ينخفض سعر المتر المربع كلما كبرت المساحة. هنا ترى مقدار الانخفاض في الحي الذي تختاره.',
    how: 'سألنا النموذج نفس السؤال عند كل مساحة شائعة لهذا النوع من العقارات. وحيث توجد صفقات فعلية قريبة من مساحة معيّنة، عرضناها بجانب الرقم المتوقّع. الدائرة الفارغة تعني أن هذه المساحة لم تُتداول في هذا الحي من قبل.',
    idle: 'اختر حياً لعرض المنحنى.',
    step1: 'قراءة المساحات المتداولة في الحي',
    step2: 'حساب سعر كل مساحة',
    step3: 'المقارنة بالصفقات الفعلية',
    resultTitle: 'حي {{district}}',
    falls: 'من {{from}} إلى {{to}} م² في هذا الحي، ينخفض سعر المتر بنحو {{pct}}%.',
    rises: 'على غير المعتاد، يرتفع سعر المتر بنحو {{pct}}% كلما كبرت المساحة هنا.',
    flat: 'سعر المتر لا يتغيّر كثيراً مع اختلاف المساحة في هذا الحي.',
  },

  a3: {
    title: 'كم يؤجَّر هذا العقار؟',
    tagline:
      'إذا نشرت الهيئة العامة للعقار إيجاراً لهذا الحي عرضناه كما هو. وإذا لم تنشر، نعرض رقماً تقديرياً ونوضّح ذلك بصراحة.',
    how: 'الإيجارات المنشورة من الهيئة تُعرض كما هي دون أي تعديل، ومعها عدد العقود. أما الأحياء التي لم تنشر الهيئة إيجاراً لها، فنعرض تقديراً مع نطاق بدل رقم واحد. لا نعدّل أبداً على رقم نشرته الهيئة.',
    coverage: '{{districts}} حي',
    type: 'نوع العقار',
    area: 'المساحة (اختياري)',
    areaHint: 'أضفها لمعرفة الإيجار الشهري',
    idle: 'اختر حياً لمعرفة إيجاره.',
    step1: 'البحث عن إيجار منشور من الهيئة',
    step2: 'استخدام التقدير إن لم يوجد',
    published_other: 'منشور من الهيئة · {{count}} عقد',
    // The other Arabic plural forms (i18next does not fall back to `_other`). A zero
    // count names no number rather than claiming there were no contracts.
    published_zero: 'منشور من الهيئة',
    published_one: 'منشور من الهيئة · عقد واحد',
    published_two: 'منشور من الهيئة · عقدان',
    published_few: 'منشور من الهيئة · {{count}} عقود',
    published_many: 'منشور من الهيئة · {{count}} عقداً',
    estimated: 'رقم تقديري: الهيئة لم تنشر إيجاراً لهذا الحي',
    perM2: 'الإيجار السنوي لكل متر مربع',
    monthly: 'الإيجار الشهري لمساحة {{area}} م²',
    estimateCaveat:
      'هذا رقم تقديري لأن الهيئة لم تنشر إيجاراً لهذا الحي وهذا النوع. حيث تنشر الهيئة، نعرض رقمها هي.',
  },

  b: {
    title: 'معلومات عن الحي',
    tagline:
      'اختر حياً واحداً وتظهر لك أربع معلومات: هل بياناته كافية، وهل ما زال أرضاً أم صار مبنياً، وحركة البيع المتوقّعة، وأثر قرب المترو.',
    how: 'كل هذه أرقام محسوبة من الصفقات المسجّلة مباشرة. «كفاية البيانات» تقيس كمية المعلومات المتوفّرة عن الحي، لا جودة الحي نفسه: قد يكون الحي ممتازاً وعدد صفقاته قليلاً. التوقّع هو الجزء الوحيد الذي يستخدم نموذجاً، وهو يتوقّع عدد الصفقات لا أسعارها.',
    idle: 'اختر حياً لعرض معلوماته.',
    step1: 'قياس كمية البيانات المتوفّرة',
    step2: 'حساب نسبة الأراضي إلى المباني',
    step3: 'قياس المسافة إلى أقرب محطة مترو',
    step4: 'توقّع عدد صفقات الربع القادم',
    risk: {
      title: 'هل بيانات هذا الحي كافية؟',
      hint: 'هذا يقيس كمية البيانات، لا جودة الحي. حي ممتاز قد تكون صفقاته قليلة.',
      clean: 'بيانات هذا الحي كاملة ولا توجد ملاحظات عليها.',
      deeds: 'عدد الصفقات',
      perQuarter: 'صفقات كل ربع',
      quarters: 'أرباع فيها نشاط',
      topDeal: 'أكبر صفقة منفردة',
      band: { low: 'كافية', medium: 'متوسطة', high: 'قليلة' },
    },
    stage: {
      title: 'هل الحي ما زال أرضاً أم صار مبنياً؟',
      note: '{{built}} من الصفقات الأخيرة عقارات مبنية، و {{land}} منها أراضٍ.',
      step: {
        land: 'أرض',
        breaking_ground: 'بدأ البناء',
        building: 'قيد البناء',
        built_out: 'مكتمل',
      },
    },
    forecast: {
      title: 'حركة البيع في الربع القادم',
      hint: 'نتوقّع عدد الصفقات التي ستُسجَّل، وليس أسعارها.',
      now: 'الربع الحالي',
      normal: 'معدّله المعتاد',
      deeds: 'صفقة',
      reverting: 'الحي يعود إلى معدّله المعتاد بعد ربع غير اعتيادي، وليس ارتفاعاً جديداً.',
      above: 'متوقّع أن يتجاوز معدّله المعتاد. 26 حياً فقط في المملكة تنطبق عليها هذه الحالة.',
      steady: 'متوقّع أن يبقى قريباً من معدّله المعتاد.',
      unconfident:
        'النطاق المتوقّع ما زال يتداخل مع عدد الربع الحالي، فلا يمكن تأكيد الاتجاه. اعتبره «بدون تغيّر يُذكر».',
    },
    metro: {
      title: 'أثر قرب المترو',
      district: 'هذا الحي يبعد {{km}} كم عن أقرب محطة، ضمن نطاق {{band}}.',
      reference: 'المرجع',
      deeds_other: '{{count}} صفقة',
      // The other Arabic plural forms (i18next does not fall back to `_other`).
      deeds_zero: 'لا صفقات',
      deeds_one: 'صفقة واحدة',
      deeds_two: 'صفقتان',
      deeds_few: '{{count}} صفقات',
      deeds_many: '{{count}} صفقة',
      caveat:
        'هذه ملاحظة وليست سبباً. خطوط المترو تُنشأ عادةً في مناطق الطلب مرتفع أصلاً، فالسعر المرتفع قد يكون سبب المترو لا نتيجته.',
    },
  },

  c1: {
    title: 'ماذا أستطيع شراءه بميزانيتي؟',
    tagline:
      'اكتب ميزانيتك، ونعرض لك الأحياء التي بيعت فيها عقارات بهذا السعر فعلاً. أسعار بيع مسجّلة، لا أسعار إعلانات.',
    how: 'نقارن ميزانيتك بـ«سعر الدخول» لكل حي، وهو السعر الذي بيع تحته ربع الصفقات. اخترنا سعر الدخول بدل المتوسط لأن المتوسط يخفي الأحياء التي فيها خيارات رخيصة متاحة.',
    coverage: '{{n}} حي',
    budget: 'ميزانيتك',
    budgetRequired: 'أدخل الميزانية بالريال',
    city: 'تحديد مدينة (اختياري)',
    sort: 'ترتيب النتائج',
    sortHeadroom: 'الأكثر فائضاً في الميزانية',
    sortPrice: 'الأرخص أولاً',
    sortLiquidity: 'الأكثر بيعاً وشراءً',
    sortDeeds: 'الأكثر بيانات',
    run: 'اعرض الأحياء',
    idle: 'اكتب ميزانيتك لعرض الأحياء المناسبة.',
    step1: 'قراءة أسعار البيع في كل حي',
    step2: 'حساب سعر الدخول لكل حي',
    step3: 'مقارنة ميزانيتك بها',
    resultTitle: '{{total}} حي في {{cities}} مدينة',
    comfortable_other: '{{count}} منها ميزانيتك تكفي حتى لمتوسط أسعارها',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    comfortable_zero: 'ميزانيتك لا تكفي لمتوسط الأسعار في أي منها',
    comfortable_one: 'واحد منها ميزانيتك تكفي حتى لمتوسط أسعاره',
    comfortable_two: 'اثنان منها ميزانيتك تكفي حتى لمتوسط أسعارهما',
    comfortable_few: '{{count}} منها ميزانيتك تكفي حتى لمتوسط أسعارها',
    comfortable_many: '{{count}} منها ميزانيتك تكفي حتى لمتوسط أسعارها',
    cheapestTitle: 'أرخص سعر دخول في المملكة',
    caption: 'الأحياء المناسبة لميزانيتك',
    colDistrict: 'الحي',
    colEntry: 'سعر الدخول',
    colMedian: 'متوسط السعر',
    colArea: 'المساحة الشائعة',
    colHeadroom: 'الفائض من ميزانيتك',
    colDeeds: 'عدد الصفقات',
    provisional: 'بعض هذه الأحياء فيها صفقات لم تُعتمد نهائياً بعد، فقد تتغيّر أرقامها.',
  },

  c4: {
    title: 'أحياء جديدة بدأت تُسجَّل فيها صفقات',
    tagline: 'أحياء لم تكن تُسجَّل فيها أي صفقات، ثم بدأت خلال السنتين الماضيتين.',
    how: 'نعدّ الحي جديداً إذا كانت أول صفقة مسجّلة له داخل المدة المعروضة، وتراكمت لديه صفقات كافية بعدها. هذا يعني أن الحي بدأ يتحرك، ولا يعني أنه خيار جيد للشراء.',
    city: 'المدينة',
    step1: 'البحث عن أول صفقة لكل حي',
    since_other: '{{count}} حي سجّل أول صفقة له منذ {{from}}',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    since_zero: 'لم يسجّل أي حي أول صفقة له منذ {{from}}',
    since_one: 'حي واحد سجّل أول صفقة له منذ {{from}}',
    since_two: 'حيّان سجّلا أول صفقة لهما منذ {{from}}',
    since_few: '{{count}} أحياء سجّلت أول صفقة لها منذ {{from}}',
    since_many: '{{count}} حياً سجّلت أول صفقة لها منذ {{from}}',
    legend: 'الرقم بجانب كل اسم هو عدد صفقاته.',
  },

  d: {
    published: 'منشور',
    estimated: 'تقديري',
    publishedTitle: 'إيجار منشور من الهيئة العامة للعقار',
    estimatedTitle: 'إيجار تقديري: الهيئة لم تنشر رقماً',
  },

  d1: {
    title: 'أين يعطي الإيجار أعلى عائد؟',
    tagline:
      'العائد الإجمالي لكل حي. لا نعرض حياً إلا إذا كان طرفا الحساب موثوقين: إيجار منشور رسمياً وأسعار بيع مسجّلة.',
    how: 'الحي لا يظهر هنا إلا إذا نشرت الهيئة إيجاره بناءً على 20 عقداً فأكثر، وسُجّلت فيه 20 صفقة بيع فأكثر. الشرطان معاً، ولهذا العدد بالمئات لا بالآلاف. العائد = الإيجار السنوي ÷ سعر الشراء، قبل أي مصاريف.',
    coverage: '{{n}} حي',
    type: 'نوع العقار',
    allTypes: 'كل الأنواع',
    minYield: 'أقل عائد مقبول',
    minYieldHint: 'نسبة مئوية',
    any: 'الكل',
    step1: 'مطابقة الإيجارات المنشورة بأسعار البيع',
    step2: 'استبعاد الأحياء ناقصة البيانات',
    resultTitle_other: '{{count}} حي',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    resultTitle_zero: 'لا توجد أحياء',
    resultTitle_one: 'حي واحد',
    resultTitle_two: 'حيّان',
    resultTitle_few: '{{count}} أحياء',
    resultTitle_many: '{{count}} حياً',
    caption: 'العائد الإجمالي لكل حي',
    colDistrict: 'الحي',
    colYield: 'العائد الإجمالي',
    colYears: 'سنوات الإيجار',
    colRent: 'الإيجار الشهري',
    colPrice: 'سعر الشراء',
    colEvidence: 'البيانات',
    contracts_other: '{{count}} عقد',
    deeds_other: '{{count}} صفقة',
    // The other Arabic plural forms (i18next does not fall back to `_other`). REGA
    // publishes 0 when it withholds a figure, so a zero contract count says exactly that.
    contracts_zero: 'عدد العقود غير منشور',
    contracts_one: 'عقد واحد',
    contracts_two: 'عقدان',
    contracts_few: '{{count}} عقود',
    contracts_many: '{{count}} عقداً',
    deeds_zero: 'لا صفقات',
    deeds_one: 'صفقة واحدة',
    deeds_two: 'صفقتان',
    deeds_few: '{{count}} صفقات',
    deeds_many: '{{count}} صفقة',
  },

  d2: {
    title: 'أستأجر أم أشتري؟',
    tagline:
      'كم سنة من الإيجار تعادل سعر الشراء. اختر حياً، أو اتركه فارغاً لعرض ترتيب الأحياء.',
    how: 'نقسم سعر الشراء على الإيجار السنوي، وكلاهما لكل متر مربع حتى لا تؤثّر المساحة. الأحياء التي لم تنشر الهيئة إيجاراً لها نستخدم تقديراً ونكتب ذلك في الصف نفسه.',
    type: 'نوع العقار (اختياري)',
    allTypes: 'كل الأنواع',
    step1: 'مقارنة الإيجار السنوي بسعر الشراء',
    yearsTitle: 'عدد سنوات الإيجار التي تعادل سعر الشراء',
    grossYield: 'عائد إجمالي {{pct}} قبل أي مصاريف',
    monthlyRent: 'الإيجار الشهري',
    price: 'سعر الشراء',
    area: 'المساحة الشائعة',
    evidence: 'عقود / صفقات',
    restsOnEstimate:
      'الإيجار في هذا الحساب رقم تقديري، لأن الهيئة لم تنشر إيجاراً لهذا الحي وهذا النوع.',
    listTitle_other: '{{count}} حي',
    restingCount_other: '{{count}} منها يعتمد على إيجار تقديري لا منشور',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    listTitle_zero: 'لا توجد أحياء',
    listTitle_one: 'حي واحد',
    listTitle_two: 'حيّان',
    listTitle_few: '{{count}} أحياء',
    listTitle_many: '{{count}} حياً',
    restingCount_zero: 'لا يعتمد أي منها على إيجار تقديري',
    restingCount_one: 'واحد منها يعتمد على إيجار تقديري لا منشور',
    restingCount_two: 'اثنان منها يعتمدان على إيجار تقديري لا منشور',
    restingCount_few: '{{count}} منها يعتمد على إيجار تقديري لا منشور',
    restingCount_many: '{{count}} منها يعتمد على إيجار تقديري لا منشور',
    caption: 'سنوات الإيجار لكل حي',
    colDistrict: 'الحي',
    colYears: 'سنوات الإيجار',
    colYield: 'العائد الإجمالي',
    colRent: 'الإيجار الشهري',
    colPrice: 'سعر الشراء',
  },

  e1: {
    title: 'أين زادت قيمة الصفقات؟',
    tagline: 'إجمالي قيمة الصفقات في آخر أربعة أرباع، مقارنة بالأربعة التي قبلها.',
    how: 'نجمع قيمة كل الصفقات المسجّلة في كل مدينة أو حي على مدى سنة، ونقارنها بالسنة السابقة. المدن التي تغيّر رقمها بسبب تغيّر في طريقة تسجيل الصفقات نعرضها في جدول منفصل، لأن ذلك تغيّر إداري وليس تغيّراً في السوق.',
    byCity: 'حسب المدينة',
    byDistrict: 'حسب الحي',
    step1: 'جمع قيمة الصفقات لكل مكان',
    step2: 'فصل الأماكن التي تغيّر فيها نظام التسجيل',
    window: '{{last}} مقابل {{prev}}',
    truncated:
      'هذه أكبر {{count}} حالة تغيّر، وليست قائمة شاملة لكل المملكة. غياب مكان عن القائمة لا يعني أنه لم يتغيّر.',
    caption: 'تغيّر قيمة الصفقات',
    colName: 'المكان',
    colChange: 'نسبة التغيّر',
    colValue: 'قيمة الصفقات',
    colDeeds: 'عدد الصفقات',
    colFlags: 'ملاحظات',
    oneDeal: 'صفقة واحدة كبيرة',
    provisional: 'غير معتمد نهائياً',
    affectedTitle: 'تغيّر بسبب نظام التسجيل لا بسبب السوق',
    affectedNote:
      'هذه الأماكن تغيّرت أرقامها لأن طريقة تسجيل الصفقات تغيّرت، لا لأن السوق تحرّك. عرضناها هنا بدل إخفائها، وأبعدناها عن الجدول أعلاه حتى لا تختلط بالنتائج الحقيقية.',
    breakAt: 'تغيّر التسجيل في {{period}}',
  },

  e2: {
    title: 'كم تبلغ أسعار الفلل؟',
    tagline:
      'أرقام الفلل المنشورة من الهيئة العامة للعقار. هذا هو المصدر الوحيد الممكن لسعر فيلا.',
    how: 'وزارة العدل تسجّل الفلل والأدوار والتاون هاوس كلها تحت بند «أرض»، فلا يمكن استخراج سعر فيلا من صفقاتها. كان بإمكاننا التخمين، لكن التخمين يختلط بالبيانات الحقيقية ولا يعود أحد يميّزه. لذلك أسعار الفلل تأتي من الهيئة أو لا تظهر أصلاً، ولهذا السبب أداة تقدير السعر في الأعلى لا تعطي سعراً للفلل.',
    district: 'الحي (اختياري)',
    cityFallbackHint: 'اختر المدينة فقط لرقم المدينة، أو أضف الحي إن كانت الهيئة نشرت رقماً له.',
    idle: 'اختر مدينة لعرض أسعار الفلل فيها.',
    step1: 'البحث عن رقم منشور من الهيئة',
    levelDistrict: 'رقم هذا الحي',
    levelCity: 'رقم المدينة',
    ppm: 'سعر المتر · {{period}}',
    deals_other: 'من {{count}} صفقة منشورة',
    // The other Arabic plural forms (i18next does not fall back to `_other`). REGA
    // publishes 0 when it withholds a figure, so a zero says the count is not published.
    deals_zero: 'عدد الصفقات غير منشور',
    deals_one: 'من صفقة منشورة واحدة',
    deals_two: 'من صفقتين منشورتين',
    deals_few: 'من {{count}} صفقات منشورة',
    deals_many: 'من {{count}} صفقة منشورة',
    vsTypical: '{{pct}} مقارنة بالمتوسط العام',
    history: 'الأرقام السابقة المنشورة',
    caveat:
      'هذا رقم الهيئة كما نشرته دون تعديل. لا تقارنه مباشرة بالنطاقات في بقية الصفحة، لأنها مبنية على سجل مختلف.',
  },

  e3: {
    title: 'أين أبحث عن أرض؟',
    tagline:
      'قائمة أحياء مع أسباب اختيارها، ومع الملاحظات التي عليها. كل سبب رقم محسوب من صفقات فعلية، وليس توقّعاً للسعر.',
    how: 'نأخذ خمسة مقاييس لكل حي فيه بيانات كافية، ويدخل الحي القائمة إذا تحقّق ثلاثة منها على الأقل. كل حي يظهر معه ما له وما عليه. الملاحظات السلبية لا يمكن إخفاؤها بأي فلتر، لأن قائمة بلا ملاحظات سلبية تكون إعلاناً لا معلومة.',
    coverage: '{{n}} حي',
    driver: 'يجب أن يتوفّر فيه',
    anyDriver: 'أي سبب',
    step1: 'حساب خمسة مقاييس لكل حي',
    step2: 'استبعاد الأحياء التي تغيّر نظام تسجيلها',
    step3: 'جمع الملاحظات السلبية',
    resultTitle_other: '{{count}} حي',
    withWarning_other: '{{count}} منها عليه ملاحظة',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    resultTitle_zero: 'لا توجد أحياء',
    resultTitle_one: 'حي واحد',
    resultTitle_two: 'حيّان',
    resultTitle_few: '{{count}} أحياء',
    resultTitle_many: '{{count}} حياً',
    withWarning_zero: 'لا توجد ملاحظات على أي منها',
    withWarning_one: 'واحد منها عليه ملاحظة',
    withWarning_two: 'اثنان منها عليهما ملاحظة',
    withWarning_few: '{{count}} منها عليه ملاحظة',
    withWarning_many: '{{count}} منها عليه ملاحظة',
    vsCity: '{{pct}} مقارنة بالمدينة',
    reasons: 'ما له وما عليه',
    for: 'ما يميّزه',
    against: 'ملاحظات عليه',
    noneAgainst: 'لا توجد ملاحظات سلبية',
    excludedNote: 'استبعدنا {{count}} حياً إضافياً لتغيّر نظام تسجيلها. قائمتها في الأسفل.',
  },

  f1: {
    title: 'أحياء استبعدناها من التقارير',
    tagline: 'أحياء تغيّرت طريقة تسجيل صفقاتها، فأرقامها تعكس إجراءً إدارياً لا حركة سوق.',
    how: 'إذا تغيّر عدد صفقات حي بشكل مفاجئ يفسّره تغيّر في نظام التسجيل أكثر مما يفسّره السوق، نستبعده من القوائم بالكامل بدل عرضه مع تحذير صغير لا يقرأه أحد. ونعرض قائمتها هنا حتى تعرف بالضبط ما استُبعد.',
    filter: 'ابحث عن حي',
    filterHint: 'اتركه فارغاً لعرض أول 150',
    filterPlaceholder: 'اسم الحي أو المدينة',
    step1: 'عرض قائمة المستبعدين',
    count_other: 'استُبعد {{count}} حي',
    // The other Arabic plural forms (i18next does not fall back to `_other`).
    count_zero: 'لم يُستبعد أي حي',
    count_one: 'استُبعد حي واحد',
    count_two: 'استُبعد حيّان',
    count_few: 'استُبعد {{count}} أحياء',
    count_many: 'استُبعد {{count}} حياً',
    noMatch: 'لا توجد نتائج',
  },

  f2: {
    title: 'متى تغيّرت البيانات لا السوق؟',
    tagline:
      'أرقام تغيّرت فجأة بشكل كبير، والسبب الأرجح تغيّر في طريقة نشر البيانات وليس تحرّك المشترين والبائعين.',
    how: 'عندما يقفز رقم فجأة، نتحقّق كم رقماً آخر قفز في نفس الربع. مكان واحد يتحرك وحده = سوق. تسعة أماكن تتحرك معاً في نفس الربع = تغيّر في النظام. أوضح مثال الرياض، التي انتقلت إلى السجل العقاري في الربع الثاني 2025 وتظهر على الورق وكأنها أكبر هبوط في المملكة.',
    show: 'اعرض',
    onlyFeed: 'التغيّرات الإدارية فقط',
    allBreaks: 'كل التغيّرات',
    step1: 'فحص كل سلسلة أرقام',
    step2: 'حساب كم سلسلة تغيّرت معاً',
    caption: 'التغيّرات المرصودة',
    colName: 'المكان',
    colShift: 'نسبة التغيّر',
    colBeforeAfter: 'قبل / بعد',
    colCause: 'السبب الأرجح',
    colTogether: 'تغيّرت معه',
    before: 'قبل',
    after: 'بعد',
    feed: 'نظام التسجيل',
    market: 'السوق',
    level: { city: 'مدينة', district: 'حي' },
    caveat:
      'وجود تغيّر لا يعني أن الرقم خاطئ. يعني أن ما قبل التغيّر وما بعده لا يقيسان نفس الشيء، فالمقارنة بينهما مضلّلة.',
  },

  f3: {
    title: 'أسعار مكرّرة تبدو محدّدة مسبقاً',
    tagline: 'أحياء وأرباع تكرّر فيها نفس سعر المتر بالضبط أكثر من أن يكون ذلك صدفة سوق.',
    how: 'هذا حكم على البيانات نفسها، لا على الحي، ولهذا لا نعرضه للعامة. أهميته أن هذه الصفقات تدخل ضمن نفس المجموعة التي تُحسب منها كل الأسعار في الموقع، فمعرفة أين تتركّز تساعد على تقدير مدى الثقة برقم معيّن.',
    step1: 'البحث عن أسعار مكرّرة',
    summary: '{{groups}} حالة تشمل {{deeds}} صفقة',
    caption: 'حالات الأسعار المكرّرة',
    colDistrict: 'الحي',
    colRate: 'السعر المكرّر',
    colShare: 'نسبته من الربع',
    colDeeds: 'عدد الصفقات',
    colShape: 'النمط',
  },

  f4: {
    title: 'إعلانات تحتاج مراجعة',
    tagline:
      'أرقام صكوك مكرّرة، وتراخيص منتهية، وأسعار غير منطقية. ملاحظات لمراجع بشري، ولا يُتّخذ أي إجراء تلقائي.',
    how: 'نفحص أحدث الإعلانات المنشورة ونقارنها ببعضها وبأسعار السوق المسجّلة. لا شيء هنا إثبات مخالفة، ولا يُحذف أو يُخفى أي إعلان تلقائياً. ولا نضع ملاحظة على السعر إلا إذا كانت بيانات الحي كافية، والمساحة متداولة فيه، والرقم متوافقاً مع صفقاته.',
    severity: 'اعرض',
    highOnly: 'المهم فقط',
    anyFlag: 'كل الملاحظات',
    step1: 'قراءة أحدث الإعلانات',
    step2: 'فحص أرقام الصكوك والتراخيص',
    step3: 'مقارنة الأسعار بالسوق المسجّل',
    resultTitle: '{{total}} إعلان عليه ملاحظة من {{scanned}} فُحصت',
    capped: 'توقّف الفحص عند {{max}} إعلان، فهذه نتيجة جزئية لا شاملة.',
    clean: 'لا توجد ملاحظات على الإعلانات الأخيرة.',
    noPriceCheck: 'تعذّر فحص السعر لهذا الإعلان ({{reason}}).',
  },

  models: {
    title: 'الطرق المستخدمة في هذه الصفحة',
    blurb:
      'ثلاث أدوات فقط تستخدم نموذجاً حاسوبياً. لكل واحدة منها جرّبنا 18 طريقة مختلفة على نفس البيانات واخترنا الأفضل، وقارنّا النتيجة بالطريقة البسيطة التي تحلّ محلها. وبقية الصفحة حسابات مباشرة على الصفقات المسجّلة.',
    estimate: 'سعر المتر حسب المساحة',
    rent: 'الإيجار حيث لا يوجد رقم منشور',
    forecast: 'عدد صفقات الربع القادم',
    miss: 'متوسط الخطأ {{a}}% مقابل {{b}}% للطريقة السابقة',
    notValuation:
      'لا شيء في هذه الصفحة تقييم عقاري. التقييم نشاط مرخّص في السعودية، وهذه الصفقات تسجّل السعر والمساحة والحي والتاريخ فقط، ولا تسجّل عدد الغرف ولا عمر المبنى ولا حالته. كل رقم هنا نطاق مبني على ما سُجّل فعلاً.',
  },

  // المعاينة: ما يراه من لا يملك صلاحية مؤشرات السوق. الأرقام حقيقية لمثال واحد،
  // والمدخلات ثابتة (pages/market-tools/MarketDemoContext.tsx).
  demo: {
    exampleTag: 'مثال',
    locked: 'مدخلات المثال ثابتة في المعاينة',
    forbidden: {
      title: 'هذه النتيجة مقفلة',
      body: 'تغيير المدينة أو الحي أو المدخلات يحتاج اشتراكاً في باقة مؤهلة أو إضافة «مؤشرات السوق».',
    },
    capped: 'تعرض المعاينة أول {{shown}} من {{total}}.',
  },

  // حين لا تتوفّر معاينة، أو يتعذّر التحقق من صلاحية الزائر.
  access: {
    lockedTitle: 'مؤشرات السوق للمشتركين',
    lockedBody:
      'أسعار الصفقات المسجّلة والإيجارات لكل حي متاحة باشتراك في باقة مؤهلة أو بإضافة «مؤشرات السوق».',
    checkFailedTitle: 'تعذّر التحقق من صلاحية الوصول',
    checkFailedBody: 'تحقّق من اتصالك بالإنترنت ثم أعد المحاولة.',
    // البطاقة أسفل المعاينة المتلاشية، لزائر لم يسجّل الدخول.
    previewSignInTitle: 'سجّل الدخول لمتابعة مؤشرات السوق',
    previewSignInBody:
      'ما سبق مثال واحد ببيانات حقيقية. سجّل الدخول أو أنشئ حساباً لاختيار مدينتك وحيّك واستخدام جميع الأدوات، مع اشتراك مؤهل أو إضافة «مؤشرات السوق».',
  },
};

export default marketConsole;
